import * as THREE from 'three';
import { useCallback, useRef } from 'react';
import { CardNames } from 'shared';

interface PureActions {
  none: null;
  shake: THREE.AnimationAction;
  rock: THREE.AnimationAction;
  paper: THREE.AnimationAction;
  scissors: THREE.AnimationAction;
}

type AnimationTiming = {
  current: number;
  total: number;
};

interface Undefined {
  activated: false;
}

interface Defined {
  activated: true;
  actions: PureActions;
  activeAction: keyof PureActions;
  nextAction?: keyof PureActions;
  afterPlaying?: Callback;
  antiDelay?: {
    timeout: number;
    callback: Callback;
  };
  playing: boolean;
  mixer: THREE.AnimationMixer;
}

type State = Undefined | Defined;

type Callback = () => any;

const animationSpeed = 0.5;

export interface IUseGlbAnimations {
  activate: (
    animations: THREE.AnimationClip[],
    modelRef: THREE.Group
  ) => THREE.AnimationMixer;
  getIsActivated: () => boolean;
  byCard: (card: CardNames, callback?: Callback, antiDelay?: number) => void;
  toNone: (callback?: Callback) => void;
  toShake: (callback?: Callback) => void;
  toRock: (callback?: Callback) => void;
  toPaper: (callback?: Callback) => void;
  toScissors: (callback?: Callback) => void;
  everyFrame: () => void;
}

export const useGlbAnimations = (): IUseGlbAnimations => {
  const state = useRef<State>({ activated: false });

  const handleLoopEnd = useCallback(() => {
    if (state.current.activated && state.current.nextAction !== undefined) {
      makeTransition(state.current.nextAction);
      state.current.nextAction = undefined;
    }
  }, []);

  const handleAnimationFinish = useCallback(() => {
    if (state.current.activated) {
      if (state.current.nextAction !== undefined) {
        handleLoopEnd();
      } else {
        state.current.playing = false;
        if (state.current.afterPlaying) {
          state.current.afterPlaying();
          state.current.afterPlaying = undefined;
        }
      }
    }
  }, []);

  const activate = (
    animations: THREE.AnimationClip[],
    model: THREE.Group
  ): THREE.AnimationMixer => {
    if (!state.current.activated) {
      const mixer = new THREE.AnimationMixer(model);
      mixer.addEventListener('loop', () => {
        handleLoopEnd();
      });
      mixer.addEventListener('finished', () => {
        handleAnimationFinish();
      });
      const actions: PureActions = {
        none: null,
        shake: mixer
          .clipAction(animations[3])
          .setEffectiveTimeScale(animationSpeed),
        rock: mixer
          .clipAction(animations[2])
          .setEffectiveTimeScale(animationSpeed)
          .setLoop(THREE.LoopOnce, 1),
        paper: mixer
          .clipAction(animations[1])
          .setEffectiveTimeScale(animationSpeed)
          .setLoop(THREE.LoopOnce, 1),
        scissors: mixer
          .clipAction(animations[0])
          .setEffectiveTimeScale(animationSpeed)
          .setLoop(THREE.LoopOnce, 1)
      };

      actions.rock.clampWhenFinished = true;
      actions.paper.clampWhenFinished = true;
      actions.scissors.clampWhenFinished = true;

      state.current = {
        activated: true,
        actions,
        mixer,
        activeAction: 'none',
        nextAction: undefined,
        afterPlaying: undefined,
        playing: false
      };

      return mixer;
    }
    return state.current.mixer;
  };

  const makeTransition = (name: keyof PureActions) => {
    if (state.current.activated) {
      stopCurrentAction();
      if (name !== 'none') {
        launchNewAction(name);
        state.current.playing = true;
      } else {
        state.current.playing = false;
        if (state.current.afterPlaying) {
          state.current.afterPlaying();
          state.current.afterPlaying = undefined;
        }
      }
      state.current.activeAction = name;
    }
  };

  const stopCurrentAction = () => {
    const s = state.current;
    if (s.activated && s.activeAction !== 'none') {
      s.actions[s.activeAction].fadeOut(0.1);
    }
  };

  const launchNewAction = (name: Exclude<keyof PureActions, 'none'>) => {
    const s = state.current;
    if (s.activated) {
      const newAction = s.actions[name];
      newAction.reset();
      newAction.fadeIn(0.1);
      newAction.play();
    }
  };

  const toAnimation = (
    name: keyof PureActions,
    callback?: Callback,
    antiDelay?: number
  ) => {
    if (state.current.activated) {
      if (antiDelay && antiDelay > 0 && callback) {
        state.current.antiDelay = {
          timeout: antiDelay,
          callback
        };
      } else {
        state.current.afterPlaying = callback;
      }
      // if something is playing right now
      if (state.current.playing) {
        // setting ref so when playing is done - it'll catch it
        state.current.nextAction = name;
      } else {
        makeTransition(name);
      }
    }
  };

  const toNone = (cb?: Callback) => toAnimation('none', cb);
  const toShake = (cb?: Callback) => toAnimation('shake', cb);
  const toRock = (cb?: Callback) => toAnimation('rock', cb);
  const toPaper = (cb?: Callback) => toAnimation('paper', cb);
  const toScissors = (cb?: Callback) => toAnimation('scissors', cb);

  const byCard = (card: CardNames, cb?: Callback, antiDelay?: number) => {
    toAnimation(cardToAction(card), cb, antiDelay);
  };

  const cardToAction = (card: CardNames): keyof PureActions => {
    switch (card) {
      case CardNames.ROCK:
        return 'rock';
      case CardNames.PAPER:
        return 'paper';
      case CardNames.SCISSORS:
        return 'scissors';
      default: {
        return 'shake';
      }
    }
  };

  const everyFrame = () => {
    if (state.current.activated) {
      if (state.current.antiDelay && !state.current.nextAction) {
        const timing = getCurrentAnimationTiming();
        if (
          timing &&
          timing.total - timing.current < state.current.antiDelay.timeout
        ) {
          state.current.antiDelay.callback();
          state.current.antiDelay = undefined;
        }
      }
    }
  };

  const getCurrentAnimationTiming = (): AnimationTiming | undefined => {
    if (state.current.activated) {
      const action = state.current.actions[state.current.activeAction];
      if (!action) return undefined;
      const timeScale = action.getEffectiveTimeScale();
      if (timeScale > 0) {
        return {
          current: (action.time / timeScale) * 1000,
          total: (action.getClip().duration / timeScale) * 1000
        };
      }
      return undefined;
    }
  };

  function getIsActivated() {
    return state.current.activated;
  }

  return {
    activate,
    getIsActivated,
    byCard,
    toNone,
    toShake,
    toRock,
    toPaper,
    toScissors,
    everyFrame
  };
};
